/**
 * DocumentVaultPicker — Phase G4A CP2.
 *
 * Small inline dropdown that queries `GET /api/documents?doc_type=<X>`
 * and lets the user attach ONE document from their Business Document
 * Vault to the current wizard step. Selection is stored on the
 * proposal's `wizard_data.<step>.<wizardField>` as the document id.
 *
 * When no matching docs exist for the requested `docType`, we render
 * an inline "Upload in Document Vault" link → `/documents` so the
 * user can add one without losing wizard state (the wizard PATCHes
 * on step navigation, so returning from the vault keeps their draft).
 *
 * Kept intentionally lightweight per user CP2 dispatch — a single
 * <select> element, no modal, no multi-select. If the user wants
 * richer picking they open the Document Vault directly.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, Paperclip } from "lucide-react";
import { api } from "@/api/client";

export default function DocumentVaultPicker({
  docType,
  label,
  value,
  onChange,
  testIdPrefix = "proposal-doc-picker",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/documents", {
        params: { doc_type: docType, limit: 50 },
      });
      setItems(res.data?.items || []);
    } catch (err) {
      // Non-fatal — the vault picker is optional context, not
      // required to progress. Silent-fail to an empty picker.
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [docType]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    const match = items.find((d) => d.id === value);
    return match ? match.display_name : "(document no longer available)";
  }, [items, value]);

  return (
    <div
      data-testid={`${testIdPrefix}-${docType}`}
      className="rounded-2xl border border-v2-border bg-v2-surfaceSubtle/60 p-4"
    >
      <div className="flex items-center gap-2 text-v2-caption uppercase tracking-[0.14em] text-v2-textMuted">
        <Paperclip className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>

      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-v2-body-sm text-v2-textMuted">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading documents…
        </div>
      ) : items.length === 0 ? (
        <div className="mt-3 text-v2-body-sm text-v2-textMuted">
          No matching documents on file yet.{" "}
          <Link
            to="/documents"
            data-testid={`${testIdPrefix}-${docType}-upload-link`}
            className="inline-flex items-center gap-1 text-v2-accent hover:underline"
          >
            Upload in Document Vault <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          <select
            data-testid={`${testIdPrefix}-${docType}-select`}
            value={value || ""}
            onChange={(e) => onChange(e.target.value || null)}
            className="rounded-xl border border-v2-border bg-v2-background px-3 py-2 text-v2-body-sm text-v2-text focus:outline-none focus:ring-2 focus:ring-v2-accent/40"
          >
            <option value="">— Not attached —</option>
            {items.map((d) => (
              <option key={d.id} value={d.id}>
                {d.display_name}
              </option>
            ))}
          </select>
          {value && selectedLabel && (
            <p className="text-v2-caption text-v2-textMuted">
              Attached: <span className="text-v2-text">{selectedLabel}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
